import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import { MANAGER_KIND, staffDisplayLabel } from './staffRoleUtils';
import {
  ADMIN_PANEL_TAB_DEFS,
  normalizePanelTabIds,
} from './adminPanelTabs';
import './YetkilendirmePage.css';

const TAB_GROUPS = [...new Set(ADMIN_PANEL_TAB_DEFS.map((t) => t.group))];

function tabsEqual(a, b) {
  const left = [...normalizePanelTabIds(a)].sort();
  const right = [...normalizePanelTabIds(b)].sort();
  if (left.length !== right.length) return false;
  return left.every((id, i) => id === right[i]);
}

function roleKey(row) {
  if (row.staff_role === 'barista') return 'barista';
  return row.manager_kind === MANAGER_KIND.MD ? 'md' : 'vm';
}

function avatarClass(row) {
  const key = roleKey(row);
  if (key === 'md') return 'yetki-avatar is-md';
  if (key === 'vm') return 'yetki-avatar is-vm';
  return 'yetki-avatar';
}

export default function YetkilendirmePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setDrafts({});
    setSaveError('');
    setSuccess('');
    const { data, error: qErr } = await supabase
      .from('staff_directory')
      .select('id, full_name, email, staff_role, manager_kind, auth_user_id, admin_panel_tabs')
      .in('staff_role', ['barista', 'manager'])
      .order('full_name');
    if (qErr) {
      setError(qErr.message);
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const barista = rows.filter((r) => roleKey(r) === 'barista').length;
    const vm = rows.filter((r) => roleKey(r) === 'vm').length;
    const md = rows.filter((r) => roleKey(r) === 'md').length;
    const withAccount = rows.filter((r) => r.auth_user_id).length;
    const without = rows.length - withAccount;
    return { total: rows.length, barista, vm, md, withAccount, without };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return rows.filter((row) => {
      if (roleFilter !== 'all' && roleKey(row) !== roleFilter) return false;
      if (accountFilter === 'account' && !row.auth_user_id) return false;
      if (accountFilter === 'none' && row.auth_user_id) return false;
      if (!q) return true;
      return [row.full_name, row.email]
        .filter(Boolean)
        .some((v) => String(v).toLocaleLowerCase('tr-TR').includes(q));
    });
  }, [rows, search, roleFilter, accountFilter]);

  const didAutoSelect = useRef(false);

  useEffect(() => {
    if (!didAutoSelect.current && filteredRows.length) {
      const firstAccount = filteredRows.find((r) => r.auth_user_id);
      setSelectedId((firstAccount || filteredRows[0]).id);
      didAutoSelect.current = true;
      return;
    }
    if (selectedId && !filteredRows.some((r) => r.id === selectedId)) {
      setSelectedId(null);
    }
  }, [filteredRows, selectedId]);

  const selected = rows.find((r) => r.id === selectedId) || null;
  const savedTabs = selected ? normalizePanelTabIds(selected.admin_panel_tabs) : [];
  const selectedTabs = selected
    ? (drafts[selected.id] ?? savedTabs)
    : [];
  const dirty = selected ? !tabsEqual(selectedTabs, savedTabs) : false;
  const hasAccount = Boolean(selected?.auth_user_id);

  const setTabsFor = (id, next) => {
    setDrafts((prev) => ({ ...prev, [id]: normalizePanelTabIds(next) }));
    setSaveError('');
    setSuccess('');
  };

  const toggleTab = (tabId) => {
    if (!selected || !hasAccount) return;
    const next = selectedTabs.includes(tabId)
      ? selectedTabs.filter((id) => id !== tabId)
      : [...selectedTabs, tabId];
    setTabsFor(selected.id, next);
  };

  const setGroup = (group, on) => {
    if (!selected || !hasAccount) return;
    const ids = ADMIN_PANEL_TAB_DEFS.filter((t) => t.group === group).map((t) => t.id);
    const next = on
      ? [...new Set([...selectedTabs, ...ids])]
      : selectedTabs.filter((id) => !ids.includes(id));
    setTabsFor(selected.id, next);
  };

  const discard = () => {
    if (!selected) return;
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[selected.id];
      return next;
    });
    setSaveError('');
    setSuccess('');
  };

  const save = async () => {
    if (!selected || !dirty) return;
    setSaving(true);
    setSaveError('');
    setSuccess('');
    const next = normalizePanelTabIds(selectedTabs);
    const { data, error: qErr } = await supabase
      .from('staff_directory')
      .update({ admin_panel_tabs: next })
      .eq('id', selected.id)
      .select()
      .single();
    setSaving(false);
    if (qErr) {
      setSaveError(qErr.message);
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
    setDrafts((prev) => {
      const copy = { ...prev };
      delete copy[data.id];
      return copy;
    });
    setSuccess('Yetkiler kaydedildi.');
  };

  const selectPerson = (row) => {
    setSelectedId(row.id);
    setSaveError('');
    setSuccess('');
  };

  return (
    <div className="admin-page-content yetkilendirme-page">
      {error ? <p className="yetki-alert yetki-alert--error">{error}</p> : null}

      <div className="yetki-toolbar">
        <div className="yetki-search-wrap">
          <svg className="yetki-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            className="yetki-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kişi veya e-posta ara…"
            aria-label="Kişi ara"
          />
        </div>
        <div className="yetki-filters" role="tablist" aria-label="Rol filtresi">
          {[
            { id: 'all', label: 'Tümü', count: counts.total },
            { id: 'barista', label: 'Barista', count: counts.barista },
            { id: 'vm', label: 'VM', count: counts.vm },
            { id: 'md', label: 'MD', count: counts.md },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={roleFilter === opt.id}
              className={`yetki-filter${roleFilter === opt.id ? ' is-active' : ''}`}
              onClick={() => setRoleFilter(opt.id)}
            >
              {opt.label}
              <em>{opt.count}</em>
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`yetki-coverage${accountFilter === 'none' ? ' is-active' : ''}`}
          onClick={() => setAccountFilter((v) => (v === 'none' ? 'all' : 'none'))}
        >
          Hesapsız {counts.without}
        </button>
        <button
          type="button"
          className="yetki-refresh"
          onClick={load}
          disabled={loading || saving}
        >
          {loading ? 'Yükleniyor…' : 'Yenile'}
        </button>
      </div>

      <div className={`yetki-layout${selected ? ' is-editing' : ''}`}>
        <aside className="yetki-people" aria-label="Personel">
          <div className="yetki-people-head">
            <h2>Personel</h2>
            <span>{counts.withAccount} hesaplı · {counts.total} kişi</span>
          </div>
          {loading ? (
            <p className="yetki-empty">Yükleniyor…</p>
          ) : filteredRows.length === 0 ? (
            <p className="yetki-empty">
              {rows.length === 0
                ? 'Ekip kaydı yok. Önce Ekip sekmesinden personel ekleyin.'
                : 'Eşleşen kişi yok.'}
            </p>
          ) : (
            <ul className="yetki-people-list">
              {filteredRows.map((row) => {
                const tabs = drafts[row.id] ?? normalizePanelTabIds(row.admin_panel_tabs);
                const isDirty = Boolean(row.auth_user_id) && !tabsEqual(tabs, row.admin_panel_tabs);
                const active = row.id === selectedId;
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      className={`yetki-person${active ? ' is-active' : ''}${row.auth_user_id ? '' : ' is-muted'}`}
                      onClick={() => selectPerson(row)}
                    >
                      <span className={avatarClass(row)} aria-hidden>
                        {(row.full_name || '?').charAt(0).toUpperCase()}
                      </span>
                      <span className="yetki-person-copy">
                        <strong>
                          {row.full_name}
                          {isDirty ? <i className="yetki-dirty-dot" title="Kaydedilmedi" /> : null}
                        </strong>
                        <span>
                          {staffDisplayLabel(row)}
                          {row.auth_user_id ? ` · ${tabs.length} sekme` : ' · hesap yok'}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <section className="yetki-detail" aria-live="polite">
          {!selected ? (
            <div className="yetki-detail-empty">
              <h2>Kişi seçin</h2>
              <p>Soldaki listeden bir ekip üyesine tıklayarak panel sekmelerini açın veya kapatın.</p>
            </div>
          ) : (
            <>
              <div className="yetki-detail-head">
                <button
                  type="button"
                  className="yetki-back"
                  onClick={() => setSelectedId(null)}
                >
                  Personel
                </button>
                <span className={avatarClass(selected)} aria-hidden>
                  {(selected.full_name || '?').charAt(0).toUpperCase()}
                </span>
                <div className="yetki-detail-copy">
                  <h2>{selected.full_name}</h2>
                  <p>
                    {staffDisplayLabel(selected)}
                    {selected.email ? ` · ${selected.email}` : ''}
                  </p>
                </div>
                {hasAccount ? (
                  <span className="yetki-count-pill">
                    {selectedTabs.length} / {ADMIN_PANEL_TAB_DEFS.length} sekme
                  </span>
                ) : (
                  <span className="yetki-count-pill yetki-count-pill--warn">Hesap yok</span>
                )}
              </div>

              {!hasAccount ? (
                <div className="yetki-detail-empty">
                  <h2>Hesap bağlı değil</h2>
                  <p>Bu kişi panele giremez. Önce Ekip sekmesinden kayıtlı bir kullanıcı bağlayın, sonra sekmeleri açın.</p>
                </div>
              ) : (
                <>
                  <div className="yetki-groups">
                    {TAB_GROUPS.map((group) => {
                      const defs = ADMIN_PANEL_TAB_DEFS.filter((t) => t.group === group);
                      const groupOn = defs.filter((t) => selectedTabs.includes(t.id)).length;
                      const allOn = groupOn === defs.length;
                      return (
                        <div key={group} className="yetki-group">
                          <div className="yetki-group-head">
                            <div>
                              <h3>{group}</h3>
                              <p>{groupOn} / {defs.length} açık</p>
                            </div>
                            <button
                              type="button"
                              className="yetki-group-toggle"
                              onClick={() => setGroup(group, !allOn)}
                            >
                              {allOn ? 'Tümünü kapat' : 'Tümünü aç'}
                            </button>
                          </div>
                          <div className="yetki-group-list">
                            {defs.map((tab) => {
                              const on = selectedTabs.includes(tab.id);
                              return (
                                <button
                                  key={tab.id}
                                  type="button"
                                  role="switch"
                                  aria-checked={on}
                                  className={`yetki-tile${on ? ' is-on' : ''}`}
                                  disabled={saving}
                                  onClick={() => toggleTab(tab.id)}
                                >
                                  <span className="yetki-tab-label">{tab.label}</span>
                                  <span className={`yetki-switch${on ? ' is-on' : ''}`} aria-hidden>
                                    <span className="yetki-switch-knob" />
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className={`yetki-savebar${dirty ? ' is-visible' : ''}${saveError ? ' is-error' : ''}${success ? ' is-success' : ''}`}>
                    <p>
                      {saveError
                        || success
                        || (dirty ? 'Kaydedilmemiş değişiklikler var.' : 'Değişiklik yok.')}
                    </p>
                    <div className="yetki-savebar-actions">
                      <button
                        type="button"
                        className="yetki-discard"
                        disabled={!dirty || saving}
                        onClick={discard}
                      >
                        Vazgeç
                      </button>
                      <button
                        type="button"
                        className="yetki-save"
                        disabled={!dirty || saving}
                        onClick={save}
                      >
                        {saving ? 'Kaydediliyor…' : 'Kaydet'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
